import React from "react";
import CustomDataTable from "src/components/CustomDataTable";
import CustomDropdown, { dropdownActionVariants } from "src/components/CustomDropdown";
import CustomModal from "src/components/CustomModal";
import CustomSheet from "src/components/CustomSheet";
import AddStaff from "src/components/modals/AddStaff";
import EditStaffRole from "src/components/modals/EditStaffRole";
import StaffDetails from "src/components/modals/StaffDetails";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getStaff, updateStaffStatus } from "src/api/settings";
import { showSuccessToast } from "src/lib/utils";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import PermissionWrapper from "src/components/PermissionWrapper";
import "src/array";
import useHasPermissions from "src/hooks/useHasPermissions";

const TeamManagement = () => {
	return (
		<>
			<div className="tw-gap-4 pageTop tw-justify-between tw-flex-wrap">
				<h2>Team Management</h2>
				<PermissionWrapper permission="add_member">
					<div>
						<CustomModal
							title="Add New Team Member"
							trigger={<Button className="!tw-rounded-full !tw-py-2.5">Add New Team Member</Button>}
						>
							<AddStaff />
						</CustomModal>
					</div>
				</PermissionWrapper>
			</div>
			<div className="innerContent">
				<Tabs defaultValue="active">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger value="active">Active</TabsTrigger>
						<TabsTrigger value="disabled">Disabled</TabsTrigger>
					</TabsList>
					<TabsContent value="active" className="tw-pt-10">
						<Table />
					</TabsContent>
					<TabsContent value="disabled" className="tw-pt-10">
						<Table type="disabled" />
					</TabsContent>
				</Tabs>
			</div>
		</>
	);
};

const Table = ({ type = "active" }) => {
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const hasEnableMemberPermission = useHasPermissions("enable_user_account");
	const hasDisableMemberPermission = useHasPermissions("disable_user_account");
	const hasAssignRolePermission = useHasPermissions("assign_user_role");

	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const { data, isLoading } = useQuery({
		queryKey: ["teams", type, page, limit],
		queryFn: () => getStaff(type)({ page, limit }),
	});

	const staff = data?.data ?? [];
	const totalRow = data?.total;

	const { mutate } = useMutation(updateStaffStatus(type), {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["teams"]);
		},
	});

	const columns = (type) => [
		{ name: "S/N", selector: (_, index) => index + 1, width: "60px" },
		{ name: "Name", selector: (row) => `${row.first_name} ${row.last_name}`, grow: 1.75 },
		{ name: "Phone", selector: (row) => row.phone },
		{ name: "Role", selector: (row) => row.role ?? "-", style: { textTransform: "capitalize" } },
		{ name: "Store", selector: (row) => row.store_or_warehouse ?? "-", style: { textTransform: "capitalize" } },
		{
			name: "Action",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						{
							label: (
								<CustomSheet title="Staff Details" trigger={<span>Staff Details</span>}>
									<StaffDetails staff={row} />
								</CustomSheet>
							),
							asChild: true,
						},
					]
						.appendToArray(
							[
								{
									label: (
										<CustomModal title="Edit Staff Details" trigger={<span>Edit Staff Details</span>}>
											<EditStaffRole staff={row} />
										</CustomModal>
									),
									asChild: true,
								},
							],
							hasAssignRolePermission && type === "active"
						)
						.appendToArray(
							[
								{
									label: (
										<CustomConfirmDialog
											trigger={<span className={dropdownActionVariants({ variant: "destructive" })}>Disable</span>}
											mutateFn={() => mutate({ uuid: row.uuid })}
										/>
									),
									asChild: true,
								},
							],
							hasDisableMemberPermission && type === "active"
						)
						.appendToArray(
							[
								{
									label: (
										<CustomConfirmDialog
											trigger={<span className={dropdownActionVariants({ variant: "destructive" })}>Enable</span>}
											mutateFn={() => mutate({ uuid: row.uuid })}
										/>
									),
									asChild: true,
								},
							],
							hasEnableMemberPermission && type !== "active"
						)}
				/>
			),
			maxWidth: "70px",
		},
	];

	return <CustomDataTable columns={columns(type)} data={staff} progressPending={isLoading} paginationTotalRows={totalRow} />;
};

export default TeamManagement;
